import { z } from 'zod';
import { crawlFailureReasonSchema, crawlStatusSchema, importOutcomeSchema, MAX_PAGE_ADDRESSES_PER_ADD, } from '../../knowledge.js';
/**
 * A website's knowledge pages: crawl the website, add pages by address,
 * approve (select) the pages the assistant may use, and re-crawl one page
 * (`features/website-knowledge/technical.md`, API contracts). Every route needs
 * the knowledge permission (`patterns/auth-patterns.md`, 3).
 */
const websiteParams = z.object({ websiteId: z.uuid() });
// ---------------------------------------------------------------------------
// Knowledge summary
// ---------------------------------------------------------------------------
export const websiteCrawlSchema = z.object({
    id: z.uuid(),
    status: crawlStatusSchema,
    pagesFound: z.number().int().min(0),
    pagesNew: z.number().int().min(0),
    pagesFailed: z.number().int().min(0),
    /** Set only when the whole crawl failed, e.g. no domain could be reached. */
    failureReason: z.string().nullable(),
    createdAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
});
export const knowledgeSummarySchema = z.object({
    websiteName: z.string(),
    domains: z.array(z.string()),
    totalPages: z.number().int().min(0),
    selectedPages: z.number().int().min(0),
    /**
     * Page approvals this billing cycle against the plan's allowance. Every
     * approval counts, and removing a page gives none back. `limit` is null
     * when the website has no plan this cycle.
     */
    pageAllowance: z.object({
        used: z.number().int().min(0),
        limit: z.number().int().min(0).nullable(),
        /** The first day of the next cycle, when `used` starts again from 0. */
        resetsOn: z.iso.date(),
    }),
    /** The most recent whole-website crawl, running or finished; `null` before the first. */
    latestCrawl: websiteCrawlSchema.nullable(),
});
export const getKnowledgeSummary = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/knowledge/summary',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: knowledgeSummarySchema,
};
// ---------------------------------------------------------------------------
// Pages list
// ---------------------------------------------------------------------------
export const PAGES_PAGE_SIZE = 50;
/**
 * `selected` is the approved list, `not_selected` the not-approved list;
 * `needs_attention` is pages whose last crawl or re-crawl failed.
 */
export const PAGE_LIST_FILTERS = ['all', 'selected', 'not_selected', 'needs_attention'];
export const pageListFilterSchema = z.enum(PAGE_LIST_FILTERS);
export const listPagesQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(PAGES_PAGE_SIZE),
    q: z.string().trim().max(200).optional(),
    status: pageListFilterSchema.default('all'),
});
export const pageListItemSchema = z.object({
    id: z.uuid(),
    url: z.string(),
    title: z.string().nullable(),
    isSelected: z.boolean(),
    /** The last successful capture; `null` when no capture has succeeded. */
    lastCrawledAt: z.iso.datetime().nullable(),
    lastCrawlFailed: z.boolean(),
    lastCrawlFailureReason: crawlFailureReasonSchema.nullable(),
    isCrawling: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const listPagesResponseSchema = z.object({
    items: z.array(pageListItemSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
/** Address A to Z. */
export const listPages = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/pages',
    params: websiteParams,
    query: listPagesQuerySchema,
    body: z.object({}),
    response: listPagesResponseSchema,
};
// ---------------------------------------------------------------------------
// Save page selection (approve / unapprove)
// ---------------------------------------------------------------------------
export const savePageSelectionBodySchema = z
    .object({
    select: z.array(z.uuid()).max(500).default([]),
    deselect: z.array(z.uuid()).max(500).default([]),
})
    .refine((body) => body.select.length + body.deselect.length > 0, {
    message: 'Choose at least one page.',
});
export const savePageSelection = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/pages/selection',
    params: websiteParams,
    query: z.object({}),
    body: savePageSelectionBodySchema,
    response: z.void(),
};
// ---------------------------------------------------------------------------
// Start a whole-website crawl
// ---------------------------------------------------------------------------
export const startWebsiteCrawl = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/crawls',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: websiteCrawlSchema,
};
// ---------------------------------------------------------------------------
// Add pages by address
// ---------------------------------------------------------------------------
export const addPagesBodySchema = z.object({
    addresses: z
        .array(z.string().trim().min(1).max(2048))
        .min(1, 'Enter at least one page address.')
        .max(MAX_PAGE_ADDRESSES_PER_ADD, `Add up to ${MAX_PAGE_ADDRESSES_PER_ADD} addresses at a time.`),
});
export const pageImportResultSchema = z.object({
    address: z.string(),
    outcome: importOutcomeSchema,
    /** For `unreadable`: why, in the failure-reason words. */
    reason: crawlFailureReasonSchema.nullable(),
    /** For `sitemap`: how many page addresses on the website's domains it listed. */
    pagesListed: z.number().int().optional(),
});
export const pageImportSchema = z.object({
    id: z.uuid(),
    status: crawlStatusSchema,
    /** `null` while the import is queued or running. */
    results: z.array(pageImportResultSchema).nullable(),
    pagesAdded: z.number().int().min(0),
    createdAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
});
/**
 * Every address must be on one of the website's domains; the request is
 * refused with a field error per address otherwise, and nothing is added.
 */
export const addPages = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/page-imports',
    params: websiteParams,
    query: z.object({}),
    body: addPagesBodySchema,
    response: pageImportSchema,
};
export const getPageImport = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/page-imports/:importId',
    params: z.object({ websiteId: z.uuid(), importId: z.uuid() }),
    query: z.object({}),
    body: z.object({}),
    response: pageImportSchema,
};
// ---------------------------------------------------------------------------
// Re-crawl one page
// ---------------------------------------------------------------------------
export const recrawlPage = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/pages/:pageId/recrawls',
    params: z.object({ websiteId: z.uuid(), pageId: z.uuid() }),
    query: z.object({}),
    body: z.object({}),
    response: z.object({ pageId: z.uuid() }),
};
//# sourceMappingURL=knowledge.js.map